import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Stack,
  TextField,
  Switch,
  FormControlLabel,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { MenuSection } from "@/types/menus";
import { useTerminology } from "@/hooks";
import { EmptyState } from "@/components";
import { Settings02Icon } from "@hugeicons/core-free-icons";

interface Props {
  section: MenuSection | null;
  onUpdateSection: (patch: Partial<MenuSection>) => void;
}

const SectionSettingsForm = ({ section, onUpdateSection }: Props) => {
  const { t } = useTerminology();

  const TEMPLATE_OPTIONS = [
    { value: "default-filled", label: "Horizontal" },
    // { value: "default-free", label: "Tarjeta limpia sin fondo" },
    // { value: "column-filled", label: `Columna de ${t('product')} rellena` },
    // { value: "column-free", label: "Columna limpia" },
    // { value: "media-filled", label: "Multimedia relleno" },
    { value: "square-middle-price", label: "Vertical" },
    //   { value: "default-text", label: `Texto simple de ${t('product')}` },
    //   { value: "list-text", label: "Lista de texto plano" },
    //   { value: "price-down-text", label: "Texto con precio inferior" },
    //   { value: "price-up-text", label: "Texto con precio superior" },
  ];

  const [title, setTitle] = useState(section?.categoryTitle || "");

  // Sync title with section changes (e.g., if another section is selected, or title is edited inline)
  useEffect(() => {
    setTitle(section?.categoryTitle || "");
  }, [section?.category, section?.categoryTitle]);

  if (!section) {
    return (
      <EmptyState
        title="Sin Selección"
        description="Selecciona una sección para ver sus opciones"
        icon={Settings02Icon}
      />
    );
  }

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setTitle(val);
    onUpdateSection({ categoryTitle: val });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3.5}>
        <Stack spacing={1}>
          <TextField
            label="Título de la Sección"
            placeholder={t('product') === 'plato' ? "Ej. Entradas, Platos fuertes" : "Ej. Ropa, Electrónicos, Servicios"}
            fullWidth
            size="medium"
            value={title}
            onChange={handleTitleChange}
          />
        </Stack>

        <Stack spacing={1}>
          <Typography variant="subtitle2">
            Diseño de Columnas (Desktop)
          </Typography>
          <Typography variant="caption" sx={{ mt: -0.5 }}>
            Define en cuántas columnas se distribuirán los {t('product', 2)} en pantallas grandes.
          </Typography>
          <ToggleButtonGroup
            value={Number(section.columns) || 1}
            exclusive
            onChange={(_, value) => {
              if (value !== null) {
                onUpdateSection({ columns: value });
              }
            }}
            fullWidth
            color="primary"
          >
            {[1, 2, 3, 4].map((col) => (
              <ToggleButton
                key={col}
                value={col}
                sx={{
                  textTransform: "none",
                }}
              >
                {col} {col === 1 ? "Col" : "Cols"}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>

        {/* Template Style */}
        <Stack spacing={1}>
          <TextField
            label="Estilo de Tarjeta"
            select
            fullWidth
            value={section.template || "default-filled"}
            onChange={(e) => onUpdateSection({ template: e.target.value as any })}
          >
            {TEMPLATE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        {/* Visibility */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: "action.hover",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box>
            <Typography variant="subtitle2" fontWeight={600}>
              Sección Visible
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Determina si esta sección se muestra al público o no.
            </Typography>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={section.isVisible !== false}
                onChange={(e) => onUpdateSection({ isVisible: e.target.checked })}
                color="primary"
              />
            }
            label=""
            labelPlacement="start"
            sx={{ m: 0 }}
          />
        </Stack>
      </Stack>
    </Box>
  );
};

export default SectionSettingsForm;
